// TextControl.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Control } from '../ControlRenderer';
import { evaluateExpression, get, set } from '../utils/Utils'
import { FormControl, FormHelperText, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FormModeType } from '@/components/FormDataProvider';
import { useValidation } from '@/components/ValidationProvider';

interface TextControl extends Control {
  placeholder?: string;
  expression?: string;
}

interface TextControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
}

const TextControl: React.FC<TextControlProps> = ({ control, data, onDataChange, mode }) => {
  const { errors, rule, validateData, setDataContext } = useValidation();
  const theme = useTheme();

  const dataType = useMemo(() => {
    return rule?.properties && control.dataKey ? rule.properties[control.dataKey]?.type : undefined;
  }, [rule, control.dataKey]);

  const handleFieldChange = (fieldName: string, value: string) => {
    let convertedValue: any = value;

    if (value === '') {
      // Treat empty as null by default
      setSelectedValue(null);
      set(data, fieldName, null);
      onDataChange(data);
    }
    // Check if the value should be an integer
    else if (dataType === "integer") {
      if (/^-?\d*$/.test(value)) {
        setSelectedValue(value);
        convertedValue = value === '' ? null : parseInt(value);
        if (!isNaN(convertedValue))
        {
          set(data, fieldName, convertedValue);
          onDataChange(data);
        }
      }
    }
    // Check if the value should be a number (integer or float)
    else if (dataType === "number") {
      if (/^-?\d*(\.\d*)?$/.test(value)) {
        setSelectedValue(value);
        convertedValue = value === '' ? null : parseFloat(value);
        if (!isNaN(convertedValue))
        {
          set(data, fieldName, convertedValue);
          onDataChange(data);
        }
      }
    }
    // If no specific validation, accept any value
    else {
      setSelectedValue(value);
      set(data, fieldName, value);
      onDataChange(data);
    }

    validateData();
  };

  const [selectedValue, setSelectedValue] = useState<any>(get(data, control.dataKey ?? '', '') ?? '');

  const errorMessage = errors && control.dataKey ? errors[control.dataKey] : undefined;

  const textControl = control as TextControl;

  useEffect(() => {
    if (textControl.expression) {
      try {
        const result = evaluateExpression(textControl.expression, data);
        setSelectedValue(result);
      } catch (error: any) {
        setSelectedValue(error.message);
        console.error('Error evaluating expression:', error);
      }
    } else {
      setSelectedValue(get(data, control.dataKey ?? '', '') ?? '');
    }
  }, [data, control.dataKey, textControl.expression, errors, onDataChange]);

  useEffect(() => {
    if (data) {
      setDataContext(data);
    }
  }, [data, setDataContext]);

  let currentMode = control.mode ? control.mode : mode;

  if (currentMode === 'label') {
    return (
      <Typography className='eform-labelmode'>
        {selectedValue}
      </Typography>
    )
  }

  const className = `eform-text-${currentMode === 'view' ? 'readonly-' : ''}${theme.palette.mode}`;
  const style = control.minWidth ? { minWidth: control.minWidth } : {};

  return (
    <>
      {errorMessage ? (
        <FormControl variant="standard" fullWidth error><TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          error
          //label={control.label}
          //name={control.name}
          value={selectedValue}
          placeholder={textControl.placeholder}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value)}
          style={style}
          inputProps={{
            readOnly: currentMode === 'view',
            spellCheck: true,
            //pattern: validationRule?.type === "integer" ? "\\d*" : validationRule?.type === "number" ? "\\d*\\.?\\d*" : undefined // Conditionally add pattern attribute
          }}
        /><FormHelperText>{errorMessage}</FormHelperText></FormControl>
      ) : (
        <TextField
          variant="standard"
          type="text"
          className={className}
          fullWidth
          value={selectedValue}
          placeholder={textControl.placeholder}
          onChange={(e) => handleFieldChange(control.dataKey ?? '', e.target.value)}
          style={style}
          inputProps={{
            readOnly: !!textControl.expression || currentMode === 'view',
            spellCheck: true
          }}
        />)}
    </>
  );
};

export default TextControl;
